import { InitialData, Payload, TFormValues } from '../CalculationId.types';
import { FIELDS } from '../partials/Form/Form.types';

type Applicant = NonNullable<TFormValues['applicant1' | 'applicant2']>;
type ApiPayloadValue = NonNullable<Payload['applicant1' | 'applicant2']>;
type ApiInitialValue = NonNullable<InitialData['loanData']['applicant1' | 'applicant2']>;

type IncomeUtil<FormValues, ApiInitialValues, ApiPayloadValue> = {
  create: () => FormValues;
  mapForm: (data: FormValues) => ApiPayloadValue;
  mapApi: (data: ApiInitialValues) => FormValues;
};

export const CurrentIncome: IncomeUtil<
  Applicant['currentIncome'],
  ApiInitialValue['currentIncome'],
  ApiPayloadValue['currentIncome']
> = {
  create: () => {
    return {
      [FIELDS.BUSINESS_TYPE]: null,
      [FIELDS.EMPLOYED_GROSS_SALARY]: 0,
      [FIELDS.SELF_EMPLOYED_SALARY]: 0,
      [FIELDS.OTHER_INCOME_PREFIX]: {
        [FIELDS.INVESTMENT_AND_SAVINGS]: 0,
        [FIELDS.RENTAL_INCOME]: 0,
        [FIELDS.MAINTENANCE_INCOME]: 0,
        [FIELDS.FOSTERING_INCOME]: 0,
        [FIELDS.BENEFIT_INCOME]: 0,
        [FIELDS.GUARANTEED_BENEFIT]: false,
        [FIELDS.GUARANTEED_MAINTENANCE]: false,
      },
      [FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY]: {
        [FIELDS.BONUS_INCOME]: 0,
        [FIELDS.COMMISSION_INCOME]: 0,
        [FIELDS.OVERTIME_INCOME]: 0,
        [FIELDS.SHIFT_ALLOWANCE_INCOME]: 0,
        [FIELDS.IS_GUARANTEED_BONUS_INCOME]: false,
        [FIELDS.IS_GUARANTEED_COMMISSION_INCOME]: false,
        [FIELDS.IS_GUARANTEED_OVERTIME_INCOME]: false,
        [FIELDS.IS_GUARANTEED_SHIFT_ALLOWANCE_INCOME]: false,
      },
      [FIELDS.RELATED_TO_SELF_EMPLOYED_SALARY]: {
        [FIELDS.DIVIDENTS_INCOME]: 0,
      },
    };
  },

  mapForm: (data) => {
    return {
      businessType: data.businessType !== '' ? (data[FIELDS.BUSINESS_TYPE] as string) : null,
      employedGrossSalary: Number(data[FIELDS.EMPLOYED_GROSS_SALARY]),
      selfEmployedSalary: Number(data[FIELDS.SELF_EMPLOYED_SALARY]),
      investmentAndSavings: Number(data[FIELDS.OTHER_INCOME_PREFIX][FIELDS.INVESTMENT_AND_SAVINGS]),
      rentalIncome: Number(data[FIELDS.OTHER_INCOME_PREFIX][FIELDS.RENTAL_INCOME]),
      maintenanceIncome: Number(data[FIELDS.OTHER_INCOME_PREFIX][FIELDS.MAINTENANCE_INCOME]),
      fosteringIncome: Number(data[FIELDS.OTHER_INCOME_PREFIX][FIELDS.FOSTERING_INCOME]),
      benefitIncome: Number(data[FIELDS.OTHER_INCOME_PREFIX][FIELDS.BENEFIT_INCOME]),
      guaranteedBenefit: data[FIELDS.OTHER_INCOME_PREFIX][FIELDS.GUARANTEED_BENEFIT] as boolean,
      guaranteedMaintenance: data[FIELDS.OTHER_INCOME_PREFIX][FIELDS.GUARANTEED_MAINTENANCE] as boolean,
      bonus: Number(data[FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY][FIELDS.BONUS_INCOME]),
      isGuaranteedBonus: data[FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY][FIELDS.IS_GUARANTEED_BONUS_INCOME] as boolean,
      commission: Number(data[FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY][FIELDS.COMMISSION_INCOME]),
      isGuaranteedCommission: data[FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY][
        FIELDS.IS_GUARANTEED_COMMISSION_INCOME
      ] as boolean,
      overtime: Number(data[FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY][FIELDS.OVERTIME_INCOME]),
      isGuaranteedOvertime: data[FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY][
        FIELDS.IS_GUARANTEED_OVERTIME_INCOME
      ] as boolean,
      shiftAllowance: Number(data[FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY][FIELDS.SHIFT_ALLOWANCE_INCOME]),
      isGuaranteedShiftAllowance: data[FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY][
        FIELDS.IS_GUARANTEED_SHIFT_ALLOWANCE_INCOME
      ] as boolean,
      dividends: Number(data[FIELDS.RELATED_TO_SELF_EMPLOYED_SALARY][FIELDS.DIVIDENTS_INCOME]),
    };
  },

  mapApi: (data): Applicant['currentIncome'] => {
    return {
      [FIELDS.BUSINESS_TYPE]: data.businessType,
      [FIELDS.EMPLOYED_GROSS_SALARY]: data.employedGrossSalary,
      [FIELDS.SELF_EMPLOYED_SALARY]: data.selfEmployedSalary,
      [FIELDS.OTHER_INCOME_PREFIX]: {
        [FIELDS.INVESTMENT_AND_SAVINGS]: data.investmentAndSavings,
        [FIELDS.RENTAL_INCOME]: data.rentalIncome,
        [FIELDS.MAINTENANCE_INCOME]: data.maintenanceIncome,
        [FIELDS.FOSTERING_INCOME]: data.fosteringIncome,
        [FIELDS.BENEFIT_INCOME]: data.benefitIncome,
        [FIELDS.GUARANTEED_BENEFIT]: data.guaranteedBenefit,
        [FIELDS.GUARANTEED_MAINTENANCE]: data.guaranteedMaintenance,
      },
      [FIELDS.RELATED_TO_EMPLOYED_GROSS_SALARY]: {
        [FIELDS.BONUS_INCOME]: data.bonus,
        [FIELDS.COMMISSION_INCOME]: data.commission,
        [FIELDS.OVERTIME_INCOME]: data.overtime,
        [FIELDS.SHIFT_ALLOWANCE_INCOME]: data.shiftAllowance,
        [FIELDS.IS_GUARANTEED_BONUS_INCOME]: data.isGuaranteedBonus,
        [FIELDS.IS_GUARANTEED_COMMISSION_INCOME]: data.isGuaranteedCommission,
        [FIELDS.IS_GUARANTEED_OVERTIME_INCOME]: data.isGuaranteedOvertime,
        [FIELDS.IS_GUARANTEED_SHIFT_ALLOWANCE_INCOME]: data.isGuaranteedShiftAllowance,
      },
      [FIELDS.RELATED_TO_SELF_EMPLOYED_SALARY]: {
        [FIELDS.DIVIDENTS_INCOME]: data.dividends,
      },
    };
  },
};

export const FutureIncome: IncomeUtil<
  Applicant['futureIncome'],
  ApiInitialValue['futureIncome'],
  ApiPayloadValue['futureIncome']
> = {
  create: () => {
    return {
      [FIELDS.MIN_MONTHLY_TAXABLE_RETIRED_INCOME]: 0,
      [FIELDS.HAS_TAXABLE_INCOME_CONTINUING]: false,
      [FIELDS.MIN_MONTHLY_TAX_FREE_RETIRED_INCOME]: 0,
      [FIELDS.HAS_TAX_FREE_INCOME_CONTINUING]: false,
    };
  },
  mapForm: (data) => {
    return {
      [FIELDS.MIN_MONTHLY_TAXABLE_RETIRED_INCOME]: Number(data.minMonthlyTaxableRetiredIncome),
      [FIELDS.HAS_TAXABLE_INCOME_CONTINUING]: Boolean(data.hasTaxableIncomeContinuing),
      [FIELDS.MIN_MONTHLY_TAX_FREE_RETIRED_INCOME]: Number(data.minMonthlyTaxFreeRetiredIncome),
      [FIELDS.HAS_TAX_FREE_INCOME_CONTINUING]: Boolean(data.hasTaxFreeIncomeContinuing),
    };
  },
  mapApi: (data) => {
    return data;
  },
};
